var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createJsonCommandExtractor } from './framework/JsonCommandExtractor';
export class AvProMxNet {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        this.framework = framework;
        // other class properties and constructor
        this.deviceList = {};
        this.videoInputNames = {};
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.detected = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.getDetected();
            }),
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                const resp = (yield this.socket.send('config get version', (resp) => {
                    var _a;
                    return ((_a = resp.cmd) === null || _a === void 0 ? void 0 : _a.startsWith('config get version')) ? resp : undefined;
                }, tags));
                if (resp.code !== 0) {
                    this.log.warning(`received error: ${JSON.stringify(resp)}`);
                    return 'unknown';
                }
                return (_a = resp.info) !== null && _a !== void 0 ? _a : 'unknown';
            }),
        };
        this.videoMatrixRoutes = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.videoMatrixRoutes.values[key];
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                // Polling seems to lag the device, especially when the system is large. Also, polling
                // can result in stale responses to the set command. Try to reduce these issues by
                // suspending polling when switching routes.
                this.framework.suspendPolling(5000);
                const resp = (yield this.socket.send(`matrix aset Aveo:va ${value} ${key}`, (resp) => {
                    var _a;
                    return ((_a = resp.cmd) === null || _a === void 0 ? void 0 : _a.startsWith(`matrix aset Aveo:va ${value} ${key}`))
                        ? resp
                        : undefined;
                }, tags));
                if (resp.code !== 0) {
                    this.log.warning(`received error: ${JSON.stringify(resp)}`);
                    return this.state.videoMatrixRoutes.values[key];
                }
                return value;
            }),
        };
        // polling
        this.polls = [
            {
                intervalMs: 5000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    yield this.updateVideoMatrixRoutes(['getMatrixRoutes', 'poll']);
                }),
            },
            {
                intervalMs: 5 * 60 * 1000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    yield this.queryDeviceList(['getDeviceList', 'poll'], true);
                }),
            },
        ];
    }
    // phase callbacks
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            const nameResp = (yield this.socket.send(`config get name`, (resp) => {
                var _a;
                const code = (_a = resp.code) !== null && _a !== void 0 ? _a : -1;
                return code === 0 ? resp : undefined;
            }, ['getName', 'initialize']));
            if (nameResp.code !== 0)
                return 'verifyFailed';
            yield this.queryDeviceList(['getDeviceList', 'initialize']);
            yield this.updateVideoMatrixRoutes(['getMatrixRoutes', 'initialize']);
            return 'connected';
        });
    }
    // other class methods
    getDetected() {
        const videoInputs = [];
        const videoOutputs = [];
        for (const [key, device] of Object.entries(this.deviceList)) {
            if (device.is_host) {
                videoInputs.push({ code: key, external: true, name: device.id });
            }
            else {
                videoOutputs.push({ code: key, name: device.id });
            }
        }
        return { videoInputs, videoOutputs };
    }
    updateDeviceList(devices) {
        this.deviceList = {};
        for (const [key, device] of Object.entries(devices)) {
            if (device.id === undefined) {
                this.log.error(`missing id for device ${JSON.stringify(device)}`);
            }
            this.deviceList[key] = {
                is_host: device.is_host,
                id: device.id,
            };
            if (device.is_host) {
                if (device.ch === undefined) {
                    this.log.error(`missing ch for device: ${JSON.stringify(device)}`);
                }
                else {
                    this.videoInputNames[device.ch] = key;
                }
            }
        }
    }
    updateVideoMatrixRoutes(tags) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const resp = (yield this.socket.send('matrix list Aveo', (resp) => {
                return resp.cmd === 'matrix list Aveo' ? resp : undefined;
            }, tags));
            const sources = (_b = (_a = resp === null || resp === void 0 ? void 0 : resp.info) === null || _a === void 0 ? void 0 : _a.srcs) !== null && _b !== void 0 ? _b : {};
            for (const [key, value] of Object.entries(sources)) {
                if (typeof key === 'string' && typeof value === 'string') {
                    this.state.videoMatrixRoutes.update(key, value);
                }
            }
        });
    }
    queryDeviceList(tags, updateDetected = false) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const resp = (yield this.socket.send('config get devicelist', (resp) => {
                var _a;
                const code = (_a = resp.code) !== null && _a !== void 0 ? _a : -1;
                return code === 0 ? resp : undefined;
            }, tags, { sendTimeoutMs: 10000 }));
            this.updateDeviceList((_a = resp.info) !== null && _a !== void 0 ? _a : {});
            if (updateDetected)
                this.state.detected.update(this.getDetected());
        });
    }
}
// static properties
AvProMxNet.driverType = 'socketDriver';
AvProMxNet.key = '-MWUH004tYVU6EnXj9Rs';
AvProMxNet.baseSocketOptions = {
    commandExtractor: createJsonCommandExtractor({ nonJsonBehavior: 'ignore' }),
    parser: (command) => {
        try {
            const resp = JSON.parse(command);
            // Ignore responses from RS232 pass through.
            if ((resp === null || resp === void 0 ? void 0 : resp.source) !== undefined)
                return undefined;
            return resp;
        }
        catch (err) {
            return undefined;
        }
    },
    sendTimeoutMs: 4500,
    sendTerminator: '\r\n',
};
AvProMxNet.socketOptions = [
    Object.assign(Object.assign({}, AvProMxNet.baseSocketOptions), { port: 24 }),
    Object.assign(Object.assign({}, AvProMxNet.baseSocketOptions), { port: 4999 }),
];
